import { useMemo, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { fold } from '@familyhub/shared';
import { useSession } from '../session';
import { rdb, type RecipeRecord } from './db';
import { setPinned } from './actions';
import { PinIcon } from './icons';
import { GearIcon } from '../components/GearIcon';

/**
 * The Recepten module — the household's saved recipes as a photo grid. A recipe
 * box is browsed by eye ("what shall we cook?"), so the picture is the tile: the
 * cookbook/Crisp photo the importer went to trouble to store and back up, over a
 * deterministic lettered tile where there is none. Tapping a card opens the recipe
 * (view, scale, cook, add-to-list); the "+" opens the importer. Everything reads
 * from the local mirror, so it works with no signal.
 *
 * The list can be narrowed by ingredient (type to find one, tap to add it — several
 * combine, AND) and by source, and re-sorted. It opens newest-first (by the date
 * the recipe was added); a recipe is pinned to the top from its own tile, and
 * pinned recipes float above whatever sort is chosen. A recipe never cooked (but
 * added under the cook-log feature) wears a "nieuw" badge.
 */
type SortMode = 'added' | 'cooked' | 'recent';

const SORTS: { mode: SortMode; label: string }[] = [
  { mode: 'added', label: 'Nieuwste' },
  { mode: 'cooked', label: 'Meest gekookt' },
  { mode: 'recent', label: 'Laatst gekookt' },
];

export function ReceptenScreen() {
  const session = useSession();
  const navigate = useNavigate();

  const rows = useLiveQuery(
    () => rdb.recepten.filter((r) => !r.deleted_at).toArray(),
    [],
    [] as RecipeRecord[],
  );
  const pending = useLiveQuery(() => rdb.outbox.count(), [], 0);

  const [source, setSource] = useState<string | null>(null);
  const [ingKeys, setIngKeys] = useState<string[]>([]);
  const [ingQuery, setIngQuery] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('added');

  // Pinned recipes always come first, then the chosen sort within each group.
  const sorted = useMemo(() => {
    const cmp = modeComparator(sortMode);
    return [...rows].sort((a, b) => pinRank(a) - pinRank(b) || cmp(a, b));
  }, [rows, sortMode]);

  // The household's own sources, most-used first, for the filter row.
  const sources = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const s = r.source_name?.trim();
      if (s) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'nl'))
      .map(([name]) => name);
  }, [rows]);

  // Every ingredient the household has, folded to a key so "Ui"/"uien" group,
  // most-used first, with a representative label for display.
  const ingredients = useMemo(() => {
    const counts = new Map<string, { label: string; n: number }>();
    for (const r of rows) {
      const seen = new Set<string>();
      for (const ing of r.ingredients) {
        const key = ingKey(ing.name);
        if (!key || seen.has(key)) continue; // count each recipe once per ingredient
        seen.add(key);
        const cur = counts.get(key);
        if (cur) cur.n += 1;
        else counts.set(key, { label: ing.name.trim(), n: 1 });
      }
    }
    return [...counts.entries()]
      .sort((a, b) => b[1].n - a[1].n || a[1].label.localeCompare(b[1].label, 'nl'))
      .map(([key, v]) => ({ key, label: v.label }));
  }, [rows]);

  const ingLabel = useMemo(() => new Map(ingredients.map((i) => [i.key, i.label])), [ingredients]);

  const suggestions = useMemo(() => {
    const q = ingQuery.trim().toLowerCase();
    if (!q) return [];
    return ingredients
      .filter((i) => !ingKeys.includes(i.key) && i.label.toLowerCase().includes(q))
      .slice(0, 12);
  }, [ingredients, ingKeys, ingQuery]);

  // A stale source filter (its source was renamed away) shows nothing rather than
  // being wrong; ingredient filters combine with AND — a recipe must contain them all.
  const recipes = useMemo(() => {
    let list = source ? sorted.filter((r) => r.source_name?.trim() === source) : sorted;
    if (ingKeys.length > 0) {
      list = list.filter((r) => {
        const keys = new Set(r.ingredients.map((ing) => ingKey(ing.name)));
        return ingKeys.every((k) => keys.has(k));
      });
    }
    return list;
  }, [sorted, source, ingKeys]);

  const filtering = source !== null || ingKeys.length > 0;
  const clearFilters = () => {
    setSource(null);
    setIngKeys([]);
    setIngQuery('');
  };

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/" aria-label="Naar het menu">‹ Hub</Link>
        <h1>Recepten</h1>
        <div className="topbar-actions">
          <Link className="btn btn-quiet" to="/recepten/instellingen" aria-label="Recept-AI"><GearIcon /></Link>
        </div>
      </div>

      {session.status === 'in' && pending > 0 && (
        <div className="banner">
          {pending} {pending === 1 ? 'wijziging wacht' : 'wijzigingen wachten'} op synchronisatie
        </div>
      )}

      {/* Filter by ingredient — type to find one, tap to add; several combine. */}
      <div className="ingredient-filter">
        <input
          type="search"
          placeholder="Filter op ingrediënt…"
          value={ingQuery}
          onChange={(e) => setIngQuery(e.target.value)}
          aria-label="Filter op ingrediënt"
        />
        {suggestions.length > 0 && (
          <ul className="ing-suggestions">
            {suggestions.map((s) => (
              <li key={s.key}>
                <button
                  className="chip"
                  onClick={() => {
                    setIngKeys((cur) => [...cur, s.key]);
                    setIngQuery('');
                  }}
                >
                  + {s.label}
                </button>
              </li>
            ))}
          </ul>
        )}
        {ingKeys.length > 0 && (
          <div className="chips chips-inline" role="group" aria-label="Gekozen ingrediënten">
            {ingKeys.map((k) => (
              <button
                key={k}
                className="chip"
                aria-pressed={true}
                onClick={() => setIngKeys((cur) => cur.filter((x) => x !== k))}
              >
                {ingLabel.get(k) ?? k} ✕
              </button>
            ))}
          </div>
        )}
      </div>

      {sources.length > 0 && (
        <div className="chips" role="group" aria-label="Filter op bron">
          <button className="chip" aria-pressed={source === null} onClick={() => setSource(null)}>
            Alle bronnen
          </button>
          {sources.map((s) => (
            <button
              key={s}
              className="chip"
              aria-pressed={source === s}
              onClick={() => setSource((cur) => (cur === s ? null : s))}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="chips" role="group" aria-label="Sorteren">
        {SORTS.map((s) => (
          <button
            key={s.mode}
            className="chip"
            aria-pressed={sortMode === s.mode}
            onClick={() => setSortMode(s.mode)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <main>
        {recipes.length === 0 ? (
          filtering ? (
            <p className="agenda-empty">
              Geen recepten voor deze filter.{' '}
              <button className="btn btn-quiet" onClick={clearFilters}>Filter wissen</button>
            </p>
          ) : (
            <p className="agenda-empty">Nog geen recepten. Voeg er rechtsonder een toe.</p>
          )
        ) : (
          <ul className="recipe-grid">
            {recipes.map((r) => (
              <li key={r.id} className="recipe-cell">
                <button className="recipe-card" onClick={() => navigate(`/recepten/${r.id}`)}>
                  <span className="recipe-thumb" style={tintFor(r.title)} aria-hidden>
                    {r.image_path ? <img src={r.image_path} alt="" loading="lazy" decoding="async" /> : initialOf(r.title)}
                  </span>
                  {/* Outside the aria-hidden photo so it is still announced. */}
                  {isNew(r) && <span className="recipe-new">nieuw</span>}
                  <span className="recipe-card-body">
                    <span className="recipe-card-title">{r.title}</span>
                    <span className="recipe-card-sub">{recipeSub(r)}</span>
                    {r.source_name?.trim() && (
                      <span className="recipe-card-source">{r.source_name.trim()}</span>
                    )}
                  </span>
                </button>
                <button
                  className="recipe-pin-toggle"
                  aria-pressed={!!r.pinned}
                  aria-label={r.pinned ? `${r.title} losmaken` : `${r.title} vastzetten`}
                  onClick={() => setPinned(r.id, !r.pinned)}
                >
                  <PinIcon />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      <Link className="fab" to="/recepten/import">
        + Toevoegen
      </Link>
    </div>
  );
}

/* ---------------- helpers ---------------- */

const byTitle = (a: RecipeRecord, b: RecipeRecord) => a.title.localeCompare(b.title, 'nl');
const cookCount = (r: RecipeRecord): number => r.cooked_log?.length ?? 0;
const lastCookedMs = (r: RecipeRecord): number => {
  const log = r.cooked_log;
  return log && log.length > 0 ? Date.parse(log[log.length - 1]!.at) : 0;
};
/** The "date added": the client-stamped added_at, falling back to the server created_at. */
const addedMs = (r: RecipeRecord): number => Date.parse(r.added_at ?? r.created_at ?? '') || 0;

/** Pinned recipes sort ahead of the rest, whatever the chosen order. */
const pinRank = (r: RecipeRecord): number => (r.pinned ? 0 : 1);

function modeComparator(mode: SortMode): (a: RecipeRecord, b: RecipeRecord) => number {
  switch (mode) {
    case 'cooked':
      return (a, b) => cookCount(b) - cookCount(a) || byTitle(a, b);
    case 'recent':
      return (a, b) => lastCookedMs(b) - lastCookedMs(a) || byTitle(a, b);
    default: // 'added' — newest first, the opening order
      return (a, b) => addedMs(b) - addedMs(a) || byTitle(a, b);
  }
}

/** Never cooked, and added under the cook-log feature (so old recipes stay quiet). */
const isNew = (r: RecipeRecord): boolean => cookCount(r) === 0 && r.added_at != null;

/**
 * A stable key for an ingredient name: lower-cased, diacritic-stripped, whitespace
 * collapsed, then each word run through the identity-safe `fold` (so "Uien"/"ui"
 * and "Volle"/"vol" group, but no two different words are ever merged).
 */
function ingKey(name: string): string {
  const norm = name
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();
  return norm
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => fold(w))
    .join(' ');
}

/** Time + how often it has been cooked — the two facts that help pick a recipe. */
function recipeSub(r: RecipeRecord): string {
  const parts: string[] = [];
  if (r.total_minutes != null) parts.push(`${r.total_minutes} min`);
  const n = cookCount(r);
  if (n > 0) parts.push(`${n}× gekookt`);
  if (parts.length === 0) parts.push(`${r.ingredients.length} ingrediënten`);
  return parts.join(' · ');
}

/**
 * The lettered-tile colour for a recipe with no photo — the app's deterministic
 * hashed tint (same formula as `ProductThumb`), so a photoless recipe reads as
 * one tile in a coloured set rather than a broken gap, and the grid does not
 * flood with the accent green (the One Voice rule). Duplicated locally rather
 * than imported, like the module's own `datetime.ts`, to keep it independent.
 */
function hueFor(text: string): number {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) % 360_000;
  return hash % 360;
}

function tintFor(title: string): CSSProperties {
  const hue = hueFor(title);
  return { backgroundColor: `hsl(${hue} 45% 88%)`, color: `hsl(${hue} 45% 28%)` };
}

/** The first letter/digit of the title, uppercased — never a punctuation mark. */
function initialOf(name: string): string {
  for (const char of name) {
    if (/\p{L}|\p{N}/u.test(char)) return char.toLocaleUpperCase('nl-NL');
  }
  return '?';
}
